import { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Image, Spin, Popconfirm, message } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import { Search, X, Eye, Pin, PinOff, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import CommentItem from "./CommentItem";
import { callCenterApi, careOf, useCallCenter } from "../../CustomerDemo/callCenterStore";
import { meName } from "../../CustomerDemo/softphone";
import { fileToDataUrl } from "../../CustomerDemo/imageUtil";
import { normalize, sanitizeHtml } from "../../Social/socialStore";

interface NotePhoto {
    uid: string;
    url: string;
    uploading?: boolean;
}

const PAGE_SIZE = 20;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB/ảnh

/** Ghi chú nội bộ về khách (soạn bằng TinyMCE, dán ảnh trực tiếp) — lưu cục bộ theo số điện thoại của khách */
export default function CommentSection({ phones }: { phones: string[] }) {
    const s = useCallCenter();
    const [content, setContent] = useState("");
    const [search, setSearch] = useState("");
    const [employee, setEmployee] = useState<string>();
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [notePhotos, setNotePhotos] = useState<NotePhoto[]>([]);
    const phoneKey = phones.join(",");

    useEffect(() => {
        setNotePhotos([]);
        setContent("");
        setLimit(PAGE_SIZE);
    }, [phoneKey]);

    // Ghi chú ghim (thông tin quan trọng) luôn ở đầu
    const logs = useMemo(() => [...careOf(s, phones, "note")].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.at - a.at), [s, phones]);
    const me = meName();
    const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Ảnh dán từ clipboard: thu nhỏ rồi giữ dạng data URL (chưa có API upload)
    const handlePastedFiles = (files: File[]) => {
        files.forEach((file) => {
            const uid = newUid();
            setNotePhotos((prev) => [...prev, { uid, url: "", uploading: true }]);
            fileToDataUrl(file)
                .then((url) => setNotePhotos((prev) => prev.map((p) => (p.uid === uid ? { uid, url } : p))))
                .catch(() => {
                    setNotePhotos((prev) => prev.filter((p) => p.uid !== uid));
                    message.error("Không đọc được ảnh");
                });
        });
    };

    const removeNotePhoto = (uid: string) => setNotePhotos((prev) => prev.filter((x) => x.uid !== uid || x.uploading));

    const handleSend = () => {
        const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        if (!plainText && notePhotos.length === 0) {
            message.warning("Vui lòng nhập nội dung");
            return;
        }
        if (notePhotos.some((p) => p.uploading)) {
            message.warning("Vui lòng chờ ảnh xử lý xong");
            return;
        }
        const imagesHtml = notePhotos.length
            ? `<p>${notePhotos.map((p) => `<img src="${p.url}" style="max-width:140px;border-radius:8px;margin:4px 4px 0 0" />`).join("")}</p>`
            : "";
        callCenterApi.addCare(phones[0], "note", sanitizeHtml(`${content}${imagesHtml}`), meName());
        message.success("Đã lưu ghi chú");
        setContent("");
        setNotePhotos([]);
    };

    const employeeOptions = useMemo(
        () => Array.from(new Set(logs.map((l) => l.author))).map((a) => ({ value: a, label: a })),
        [logs]
    );

    const comments = useMemo(() => {
        const kw = normalize(search);
        return logs.filter((x) => {
            const text = normalize(`${x.author} ${x.content.replace(/<[^>]*>/g, " ")}`);
            return (!kw || text.includes(kw)) && (!employee || x.author === employee);
        });
    }, [logs, search, employee]);

    return (
        <div className="overflow-hidden">
            <Editor
                tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
                licenseKey="gpl"
                value={content}
                onEditorChange={setContent}
                init={{
                    height: 180,
                    menubar: false,
                    branding: false,
                    resize: false,
                    skin: document.documentElement.classList.contains("dark") ? "oxide-dark" : "oxide",
                    content_css: document.documentElement.classList.contains("dark") ? "dark" : "default",
                    plugins: ["link", "lists", "table", "code", "fullscreen", "preview"],
                    toolbar: "undo redo | bold italic underline strikethrough | forecolor backcolor | bullist numlist | table link | preview fullscreen",
                    placeholder: "Nhập ghi chú về khách (dán ảnh trực tiếp bằng Ctrl+V)",
                    paste_data_images: false,
                    setup: (editor: any) => {
                        // tinymce-react chỉ đồng bộ khi keyup/change — thêm input để không mất ký tự cuối
                        editor.on("input", () => setContent(editor.getContent()));
                        editor.on("paste", (e: ClipboardEvent) => {
                            const items = e.clipboardData?.items;
                            if (!items) return;
                            const files: File[] = [];
                            let tooLarge = false;
                            Array.from(items).forEach((item) => {
                                if (item.type.startsWith("image/")) {
                                    const file = item.getAsFile();
                                    if (!file) return;
                                    if (file.size > MAX_PHOTO_SIZE) { tooLarge = true; return; }
                                    files.push(file);
                                }
                            });
                            if (tooLarge) message.error("Ảnh vượt quá 10MB");
                            if (!files.length) return;
                            e.preventDefault();
                            handlePastedFiles(files);
                        });
                    },
                }}
            />

            {notePhotos.length > 0 && (
                <div className="flex flex-wrap gap-2 py-3">
                    {notePhotos.map((photo) => (
                        <div key={photo.uid} className="relative rounded-lg overflow-hidden border dark:border-[#2a2a2a] !w-[64px] !h-[64px]">
                            {photo.url && <Image src={photo.url} className="!w-[64px] !h-[64px] object-contain" preview={{ mask: <Eye size={18} /> }} />}
                            {photo.uploading && (
                                <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Spin size="small" /></div>
                            )}
                            <button type="button" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex justify-center items-center cursor-pointer" onClick={() => removeNotePhoto(photo.uid)}>
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-end py-3">
                <Button type="primary" className="px-8" onClick={handleSend}>Lưu ghi chú</Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 py-3 pt-0">
                <Input allowClear value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm trong ghi chú" prefix={<Search size={16} />} />
                <Select allowClear value={employee} onChange={setEmployee} placeholder="Lọc theo người viết" showSearch optionFilterProp="label" options={employeeOptions} />
            </div>

            <div className="max-h-[420px] overflow-y-auto">
                {comments.slice(0, limit).map((item) => (
                    <div key={item.id} className={`relative group ${item.pinned ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`}>
                        {item.pinned && <div className="px-3 pt-2 text-[11px] font-semibold text-amber-600 flex items-center gap-1"><Pin size={11} /> Đã ghim</div>}
                        <CommentItem
                            item={{ id: item.id, avatar: "", name: item.author, time: dayjs(item.at).format("HH:mm - DD/MM/YYYY"), content: item.content }}
                        />
                        <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1">
                            <Button size="small" type="text" icon={item.pinned ? <PinOff size={13} /> : <Pin size={13} />} title={item.pinned ? "Bỏ ghim" : "Ghim lên đầu"}
                                onClick={() => callCenterApi.togglePinCare(item.id)} />
                            {item.author === me && (
                                <Popconfirm title="Xoá ghi chú này?" okText="Xoá" cancelText="Huỷ" okButtonProps={{ danger: true }}
                                    onConfirm={() => { callCenterApi.deleteCare(item.id); message.success("Đã xoá ghi chú"); }}>
                                    <Button size="small" type="text" danger icon={<Trash2 size={13} />} title="Xoá (chỉ người viết)" />
                                </Popconfirm>
                            )}
                        </div>
                    </div>
                ))}
                {comments.length === 0 && (
                    <div className="py-10 text-center text-slate-400 font-medium">
                        {logs.length ? "Không có ghi chú khớp bộ lọc" : "Chưa có ghi chú nào"}
                    </div>
                )}
                {comments.length > limit && (
                    <div className="py-3 text-center">
                        <Button onClick={() => setLimit((l) => l + PAGE_SIZE)}>Xem thêm ({limit}/{comments.length})</Button>
                    </div>
                )}
            </div>
        </div>
    );
}
